import React, { Children, ReactElement } from 'react';
import { useRouter } from 'next/router';

// components
import Link, { LinkProps } from 'next/link';
import clsx from 'clsx';

type ChildrenProps = {
  active: boolean;
};

export type NavLinkProps = {
  activeClassName?: string;
  className?: string;
  children: ReactElement | ((props: ChildrenProps) => ReactElement);
} & LinkProps;

/**
 * @component NavLink
 * @summary basic next js router link with adition active class to the child component
 * @param {ReactElement} children child component that gets the honor
 * @param {string} activeClassName class name to represent link activation
 * @param {LinkProps} props
 */
function NavLink({
  children,
  activeClassName = `active`,
  className,
  ...props
}: NavLinkProps) {
  const { asPath } = useRouter();
  const active =
    `/${asPath.split(`/`)[1]}` === props.href || asPath === props.as;

  const child = Children.only(
    typeof children === `function` ? children({ active }) : children,
  );

  const childClassName = child?.props.className || ``;

  const innerClassName = active
    ? `${childClassName} ${activeClassName}`.trim()
    : childClassName;

  const classes = clsx(innerClassName, className);
  return (
    <Link {...props}>
      {React.cloneElement(child, {
        className: classes,
      })}
    </Link>
  );
}

export default NavLink;
